import { useQuery } from "@tanstack/react-query";
import { endImpersonation, isImpersonating } from "@/lib/impersonation";
import { Check, Loader2 } from "lucide-react";
import { useState } from "react";
import { useToast } from "@/hooks/use-toast";
// v7.10 — paywall restyled onto the shared auth theme (Dan): same cream page,
// white hairline cards and black buttons as signup. The indigo #6366F1 Pro
// treatment (border, badge, button) is gone — Pro is now our pink accent.
import {
  CARD, INK, MUTED, LINE, BTN, BTN_HOVER, PINK,
  pageStyle, monoLabelStyle, h1Style, serifStyle, buttonStyle,
} from "./auth-theme";

/* ──────────────────────────────────────────────────────────────────────────
 * Plan picker — the paywall.
 *
 * Shown to any logged-in user without an active subscription. The "Continue
 * to checkout" button is stubbed for now (Task 1) and will redirect to
 * Stripe Checkout once Task 2 is wired up.
 * ────────────────────────────────────────────────────────────────────────── */

interface Plan {
  id: number;
  name: string;
  priceCents: number;
  creditsPerMonth: number;
  features: string[];
  isArchived: boolean;
}

interface TrialEligibility {
  eligible: boolean;
  reason: string | null;
  trialDays: number;
  trialCredits: number;
  planName?: string;
  planPriceCents?: number;
}

export default function BillingSelectPlan() {
  const { toast } = useToast();
  const [selectedPlanId, setSelectedPlanId] = useState<number | null>(null);
  const [checkoutPending, setCheckoutPending] = useState(false);
  const [trialPending, setTrialPending] = useState(false);

  const { data: plans, isLoading } = useQuery<Plan[]>({
    queryKey: ["/api/billing/plans"],
    queryFn: async () => {
      const res = await fetch("/api/billing/plans", { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load plans");
      return res.json();
    },
  });

  const { data: trial } = useQuery<TrialEligibility>({
    queryKey: ["/api/billing/trial-eligibility"],
    queryFn: async () => {
      const res = await fetch("/api/billing/trial-eligibility", { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load trial eligibility");
      return res.json();
    },
  });

  async function onStartTrial() {
    setTrialPending(true);
    try {
      const res = await fetch("/api/billing/start-trial", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
      });
      const data = await res.json();
      if (!res.ok) {
        toast({ title: "Couldn't start trial", description: data.message || "Please try again.", variant: "destructive" });
        return;
      }
      if (data.adopted) {
        // A prior trial checkout already completed in Stripe — the server
        // adopted it. Reload into the app; the paywall lifts on re-auth.
        window.location.href = "/";
      } else if (data.url) {
        window.location.href = data.url;
      } else {
        toast({ title: "Unexpected response", description: "No checkout URL returned", variant: "destructive" });
      }
    } catch (err: any) {
      toast({ title: "Couldn't start trial", description: err.message, variant: "destructive" });
    } finally {
      setTrialPending(false);
    }
  }

  const visible = (plans ?? [])
    .filter(p => !p.isArchived && p.priceCents > 0) // hide Enterprise (priced $0, comp tier)
    .sort((a, b) => a.priceCents - b.priceCents);

  async function onSelect(planId: number) {
    setSelectedPlanId(planId);
    setCheckoutPending(true);
    try {
      const res = await fetch("/api/billing/checkout", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ planId }),
      });
      if (!res.ok) {
        const data = await res.json();
        // Task 2 will wire Stripe — until then, this returns a friendly stub message.
        toast({ title: "Checkout not yet wired", description: data.message || "Stripe integration pending", variant: "destructive" });
        return;
      }
      const { url } = await res.json();
      if (url) {
        window.location.href = url;
      } else {
        toast({ title: "Unexpected response", description: "No checkout URL returned", variant: "destructive" });
      }
    } catch (err: any) {
      toast({ title: "Checkout failed", description: err.message, variant: "destructive" });
    } finally {
      setCheckoutPending(false);
    }
  }

  async function onLogout() {
    // v7.54 — inside an admin "Login as" window, sign-out ends that session only.
    if (isImpersonating()) { await endImpersonation(); return; }
    await fetch("/api/auth/logout", { method: "POST", credentials: "include" });
    // Full reload so App.tsx's auth context re-fetches and lands at /login
    // cleanly. setLocation alone leaves cached user state and bounces back
    // to the paywall.
    window.location.href = "/login";
  }

  return (
    <div
      className="min-h-screen flex flex-col justify-center"
      // v7.11 (Dan: "I have to scroll to the buttons") — the whole paywall
      // now fits one screen on load. Every vertical gap is vh-clamped, so a
      // short laptop viewport compresses to the floor value while a tall
      // screen keeps the roomy spacing. Content is centred once it fits.
      style={{ ...pageStyle, padding: "clamp(20px, 4vh, 64px) 24px" }}
      data-testid="billing-select-plan"
    >
      <div className="max-w-5xl mx-auto w-full">
        <div className="text-center" style={{ marginBottom: "clamp(16px, 3.4vh, 48px)" }}>
          <div style={{ ...monoLabelStyle, marginBottom: "clamp(6px, 1.2vh, 12px)" }}>Billing</div>
          <h1 style={{ ...h1Style, fontSize: "clamp(27px, 4.4vh, 42px)" }}>
            Choose your{" "}
            <span style={{ ...serifStyle, fontSize: "clamp(29px, 4.8vh, 46px)" }}>plan.</span>
          </h1>
          <p style={{ fontSize: 13.5, color: MUTED, marginTop: "clamp(4px, 1vh, 12px)" }}>
            Pick a plan to unlock the platform. Cancel anytime.
          </p>
        </div>

        {isLoading && (
          <div className="text-center" style={{ fontSize: 13.5, color: MUTED }}>
            Loading plans…
          </div>
        )}

        {!isLoading && visible.length === 0 && (
          <div className="text-center" style={{ fontSize: 13.5, color: "#8F2748" }}>
            No plans are currently available. Please contact support.
          </div>
        )}

        {trial?.eligible && (
          <div
            className="flex flex-col md:flex-row md:items-center gap-4"
            style={{
              padding: "clamp(14px, 2.4vh, 24px)",
              marginBottom: "clamp(14px, 2.6vh, 32px)",
              backgroundColor: CARD,
              border: `1.5px solid ${PINK}`,
              borderRadius: 14,
              boxShadow: "0 6px 24px rgba(123,31,64,0.10)",
            }}
            data-testid="trial-offer-card"
          >
            <div className="flex-1">
              <h2 style={{ ...h1Style, fontSize: 24 }}>
                Try Admoji{" "}
                <span style={{ ...serifStyle, fontSize: 26 }}>free.</span>
              </h2>
              <p className="mt-1.5" style={{ fontSize: 13.5, color: MUTED }}>
                {trial.trialCredits.toLocaleString()} free credits
              </p>
              <p className="mt-2" style={{ fontSize: 11, color: MUTED }}>
                {trial.planName ?? "Starter"} at ${(((trial.planPriceCents ?? 9000)) / 100).toFixed(0)}/mo starts
                automatically after the trial. Cancel anytime
              </p>
            </div>
            <button
              onClick={onStartTrial}
              disabled={trialPending || checkoutPending}
              data-testid="start-trial-button"
              className="px-6 transition-colors disabled:opacity-60 flex-shrink-0"
              style={{ ...buttonStyle, backgroundColor: PINK }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.backgroundColor = "#8F2748"; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.backgroundColor = PINK; }}
            >
              {trialPending ? (
                <span className="inline-flex items-center justify-center">
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Redirecting…
                </span>
              ) : (
                "Try For Free"
              )}
            </button>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-3" style={{ gap: "clamp(12px, 2vh, 24px)" }}>
          {visible.map(plan => {
            const usd = plan.priceCents / 100;
            const isPro = plan.name.toLowerCase() === "pro";
            const isPicked = selectedPlanId === plan.id;
            return (
              <div
                key={plan.id}
                className="flex flex-col"
                style={{
                  padding: "clamp(16px, 2.4vh, 24px)",
                  backgroundColor: CARD,
                  // Pro is picked out in our pink, not the old indigo.
                  border: isPro ? `2px solid ${PINK}` : `1px solid ${LINE}`,
                  borderRadius: 14,
                  boxShadow: isPro ? "0 6px 24px rgba(123,31,64,0.10)" : "none",
                }}
                data-testid={`plan-card-${plan.id}`}
              >
                {isPro && (
                  <div
                    className="self-start px-2 py-1 mb-3"
                    style={{ ...monoLabelStyle, marginBottom: 12, backgroundColor: PINK, color: "#FFFFFF", borderRadius: 5 }}
                  >
                    Most popular
                  </div>
                )}
                <h2 style={{ fontSize: 17, fontWeight: 600, color: INK, letterSpacing: "-0.015em" }}>
                  {plan.name}
                </h2>
                <div className="flex items-baseline gap-1" style={{ marginTop: "clamp(6px, 1.4vh, 12px)" }}>
                  <span style={{ fontSize: "clamp(26px, 3.6vh, 34px)", fontWeight: 600, color: INK, letterSpacing: "-0.032em" }}>${usd.toFixed(0)}</span>
                  <span style={{ fontSize: 13.5, color: MUTED }}>/mo</span>
                </div>
                <div className="mt-1" style={{ fontSize: 13, color: MUTED }}>
                  {plan.creditsPerMonth.toLocaleString()} credits/month
                </div>

                <div
                  className="border-t flex-1"
                  style={{
                    borderColor: LINE,
                    marginTop: "clamp(12px, 2vh, 20px)",
                    paddingTop: "clamp(12px, 2vh, 20px)",
                    display: "flex",
                    flexDirection: "column",
                    gap: "clamp(6px, 1.2vh, 10px)",
                  }}
                >
                  {(plan.features ?? []).map((f, i) => (
                    <div key={i} className="flex items-start gap-2" style={{ fontSize: 13, lineHeight: 1.4, color: "#334155" }}>
                      <Check className="w-4 h-4 flex-shrink-0" style={{ color: INK, marginTop: 1 }} />
                      <span>{f}</span>
                    </div>
                  ))}
                </div>

                <button
                  onClick={() => onSelect(plan.id)}
                  disabled={checkoutPending}
                  data-testid={`plan-select-${plan.id}`}
                  className="w-full transition-colors disabled:opacity-60"
                  // marginTop keeps the CTA off the feature list without
                  // pushing the card past the fold on short screens.
                  style={{
                    ...buttonStyle,
                    backgroundColor: isPro ? PINK : BTN,
                    marginTop: "clamp(14px, 2.4vh, 24px)",
                    height: "clamp(40px, 5.2vh, 46px)",
                  }}
                  onMouseEnter={(e) => {
                    if (!checkoutPending) (e.currentTarget as HTMLButtonElement).style.backgroundColor = isPro ? "#8F2748" : BTN_HOVER;
                  }}
                  onMouseLeave={(e) => {
                    (e.currentTarget as HTMLButtonElement).style.backgroundColor = isPro ? PINK : BTN;
                  }}
                >
                  {checkoutPending && isPicked ? (
                    <span className="inline-flex items-center justify-center">
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Redirecting…
                    </span>
                  ) : (
                    "Continue to checkout"
                  )}
                </button>
              </div>
            );
          })}
        </div>

        <div className="text-center" style={{ fontSize: 11.5, color: MUTED, marginTop: "clamp(14px, 3vh, 48px)" }}>
          <button
            onClick={onLogout}
            className="hover:underline"
            data-testid="logout-link"
          >
            Sign out
          </button>
          <span className="mx-2">·</span>
          <a href="mailto:support@admoji.com" className="hover:underline">Need help?</a>
        </div>
      </div>
    </div>
  );
}
